import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import QuickInquiryModal from './QuickInquiryModal';
import { trackEvent } from './PageviewTracker';
import { AIFab, useAIConfig } from './AIFab';

/**
 * Floating Contact FAB Stack (bottom-right).
 * Renders a vertical column with (top→bottom): Mail (Schnellanfrage-Modal)
 * and WhatsApp. Sits above the scroll-to-top button so nothing overlaps.
 * - Uses settings.contact.whatsapp_number (digits only, intl format e.g. 491746814861)
 * - Hidden on /admin routes
 * - Shows a small pulse animation on WhatsApp + a one-time tooltip after 4s
 */
const WhatsAppButton = () => {
  const { settings } = useSettings();
  const location = useLocation();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const aiCfg = useAIConfig();
  const aiEnabled = !!aiCfg?.enabled;
  const aiLeft = aiCfg?.design?.position === 'left';

  const number = (settings?.contact?.whatsapp_number || '').replace(/\D/g, '');
  const siteName = settings?.site_name || 'Hüpfburgen Verleih';
  const prefill = encodeURIComponent(`Hallo ${siteName}, ich habe eine Frage zu Ihren Hüpfburgen.`);
  const waHref = number ? `https://wa.me/${number}?text=${prefill}` : null;

  useEffect(() => {
    if (!number || dismissed) return;
    const t = setTimeout(() => setTooltipVisible(true), 11000);
    const t2 = setTimeout(() => setTooltipVisible(false), 19000);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [number, dismissed]);

  useEffect(() => {
    const open = () => setInquiryOpen(true);
    window.addEventListener('kidsevent-open-inquiry', open);
    return () => window.removeEventListener('kidsevent-open-inquiry', open);
  }, []);

  if (location.pathname.startsWith('/admin')) return null;

  return (
    <>
      {/* Stack sits above the 44px scroll-to-top FAB (bottom:16px) with an 8px gap. */}
      <div
        className="hidden md:flex fixed right-4 z-50 flex-col items-end gap-2.5"
        style={{ bottom: '76px' }}
        data-testid="contact-fab-stack"
      >
        {/* KI-Assistent (ersetzt Mail-FAB; Formular bleibt über den Chat erreichbar) */}
        {aiEnabled ? (!aiLeft && <AIFab />) : (
        <button
          type="button"
          onClick={() => setInquiryOpen(true)}
          aria-label="Schnellanfrage per E-Mail-Formular"
          className="flex items-center justify-center w-12 h-12 rounded-full shadow-xl transition-transform hover:scale-110 active:scale-95 text-white"
          style={{ background: '#005898' }}
          data-testid="mail-fab"
        >
          <Mail className="w-5 h-5" />
        </button>
        )}

        {/* WhatsApp with pulse + tooltip */}
        {waHref && (
          <div className="relative flex items-end gap-2">
            {tooltipVisible && (
              <div
                className="hidden sm:flex items-center bg-white rounded-2xl shadow-xl px-4 py-3 w-[250px] border border-neutral-100 animate-fade-in-up absolute right-14 bottom-1"
                data-testid="whatsapp-tooltip"
              >
                <div className="text-sm">
                  <div className="font-semibold text-neutral-900 leading-tight">Fragen? Schreib uns!</div>
                  <div className="text-neutral-600 text-xs mt-0.5">Antwort meist innerhalb 1 Std.</div>
                </div>
                <button
                  onClick={() => { setTooltipVisible(false); setDismissed(true); }}
                  className="ml-2 text-neutral-400 hover:text-neutral-700 text-lg leading-none"
                  aria-label="Schließen"
                  data-testid="whatsapp-tooltip-close"
                >
                  ×
                </button>
              </div>
            )}

            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Per WhatsApp kontaktieren"
              className="relative group flex items-center justify-center w-12 h-12 rounded-full shadow-xl transition-transform hover:scale-110 active:scale-95"
              style={{ background: '#25D366' }}
              data-testid="whatsapp-fab"
              onClick={() => { setTooltipVisible(false); trackEvent('cta_whatsapp_click', { origin: 'fab' }); }}
            >
              {/* Pulsing ring */}
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full"
                style={{
                  background: '#25D366',
                  opacity: 0.4,
                  animation: 'wa-pulse 2s ease-out infinite',
                }}
              />
              {/* Official WhatsApp glyph */}
              <svg
                viewBox="0 0 32 32"
                className="relative w-6 h-6 text-white"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19.11 17.21c-.27-.13-1.6-.79-1.85-.88-.25-.09-.43-.13-.61.14-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.13-1.13-.42-2.15-1.33-.79-.71-1.33-1.58-1.49-1.85-.16-.27-.02-.41.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.02-.22-.53-.45-.46-.61-.47l-.52-.01a1 1 0 00-.72.34c-.25.27-.95.93-.95 2.27 0 1.34.97 2.63 1.11 2.81.14.18 1.91 2.92 4.63 4.1.65.28 1.16.45 1.55.58.65.21 1.24.18 1.7.11.52-.08 1.6-.65 1.83-1.28.23-.63.23-1.18.16-1.29-.07-.11-.25-.18-.52-.31zM16.05 5.33c-5.86 0-10.62 4.76-10.63 10.61 0 1.87.49 3.69 1.42 5.3l-1.51 5.51 5.65-1.48a10.6 10.6 0 005.07 1.29h.01c5.86 0 10.62-4.76 10.63-10.61a10.55 10.55 0 00-3.11-7.51 10.56 10.56 0 00-7.53-3.11zm0 19.41h-.01a8.81 8.81 0 01-4.49-1.23l-.32-.19-3.34.88.89-3.27-.21-.33a8.79 8.79 0 01-1.35-4.69c.01-4.85 3.96-8.8 8.83-8.8 2.36 0 4.57.92 6.24 2.59a8.74 8.74 0 012.58 6.22c-.01 4.85-3.96 8.82-8.82 8.82z"/>
              </svg>
            </a>
          </div>
        )}
      </div>

      <QuickInquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
    </>
  );
};

export default WhatsAppButton;
